import React, { useState, useEffect } from 'react';
import { MessageSquare, Mail, Phone, Calendar, Send } from 'lucide-react';
import { professionalClientService } from '../../../services/professionalClientService';

const CommunicationsTab = ({ clientId }) => {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewComm, setShowNewComm] = useState(false);
  const [newComm, setNewComm] = useState({
    type: 'note',
    subject: '',
    message: ''
  });

  useEffect(() => {
    loadCommunications();
  }, [clientId]);

  const loadCommunications = async () => {
    try {
      setLoading(true);
      const data = await professionalClientService?.getClientCommunications(clientId);
      setCommunications(data);
    } catch (error) {
      console.error('Error loading communications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendCommunication = async () => {
    if (!newComm?.message) {
      alert('Veuillez saisir un message');
      return;
    }

    try {
      await professionalClientService?.createCommunication(clientId, newComm);
      setNewComm({ type: 'note', subject: '', message: '' });
      setShowNewComm(false);
      await loadCommunications();
    } catch (error) {
      console.error('Error creating communication:', error);
      alert('Erreur lors de l\'enregistrement de la communication');
    }
  };

  const getCommIcon = (type) => {
    switch (type) {
      case 'email':
        return <Mail className="w-5 h-5 text-blue-600" />;
      case 'phone':
        return <Phone className="w-5 h-5 text-green-600" />;
      case 'meeting':
        return <Calendar className="w-5 h-5 text-purple-600" />;
      default:
        return <MessageSquare className="w-5 h-5 text-gray-600" />;
    }
  };

  const getCommTypeLabel = (type) => {
    const labels = {
      email: 'Email',
      phone: 'Appel téléphonique',
      meeting: 'Réunion',
      notification: 'Notification',
      note: 'Note interne'
    };
    return labels?.[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        <p className="ml-4 text-gray-600">Chargement des communications...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Communications</h2>
        <button
          onClick={() => setShowNewComm(!showNewComm)}
          className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <Send className="w-4 h-4 mr-2" />
          Nouvelle communication
        </button>
      </div>
      {/* New Communication Form */}
      {showNewComm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
          <h3 className="font-medium text-gray-900 mb-4">Enregistrer une communication</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={newComm?.type}
                onChange={(e) => setNewComm({ ...newComm, type: e?.target?.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="note">Note interne</option>
                <option value="email">Email</option>
                <option value="phone">Appel téléphonique</option>
                <option value="meeting">Réunion</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sujet (optionnel)</label>
              <input
                type="text"
                value={newComm?.subject}
                onChange={(e) => setNewComm({ ...newComm, subject: e?.target?.value })}
                placeholder="Sujet de la communication"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
              <textarea
                value={newComm?.message}
                onChange={(e) => setNewComm({ ...newComm, message: e?.target?.value })}
                rows={4}
                placeholder="Détails de la communication..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSendCommunication}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Enregistrer
              </button>
              <button
                onClick={() => setShowNewComm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Communications List */}
      {communications?.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">Aucune communication enregistrée</p>
        </div>
      ) : (
        <div className="space-y-4">
          {communications?.map((comm) => (
            <div key={comm?.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                {getCommIcon(comm?.communication_type)}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{getCommTypeLabel(comm?.communication_type)}</span>
                    <span className="text-sm text-gray-500">
                      {new Date(comm?.sent_at)?.toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  {comm?.subject && (
                    <p className="font-medium text-gray-800 mb-1">{comm?.subject}</p>
                  )}
                  <p className="text-gray-600 text-sm">{comm?.message}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommunicationsTab;